import { getBaseUrl } from "../typed-client";

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Extracts the first page number from location_information.
 * E.g.: "Page(s) 3-5" -> 3, "Page 12" -> 12
 */
export function extractPageNumber(locationInfo: string | null | undefined): number | null {
  if (!locationInfo) return null;

  const match = locationInfo.match(/Page\(?s?\)?\s*(\d+)/i);

  return match ? parseInt(match[1], 10) : null;
}

/**
 * Checks if a URL is an internal API URL that requires authentication.
 */
export function isApiUrl(url: string | null | undefined): boolean {
  if (!url) return false;

  const apiBaseUrl = getBaseUrl();

  return url.startsWith("/api/") || url.startsWith(`${apiBaseUrl}/api/`);
}

/**
 * Builds an accessible URL for a document source.
 *
 * - HTTP/HTTPS URLs (OneDrive): returned as-is
 * - API URLs (/api/download/...): returned as-is (requires authenticated fetch)
 * - Local paths (Docker): transformed to backend URL /api/documents/download/{path}
 * - Adds #page=N for direct PDF page navigation
 */
export function buildDocumentUrl(
  sourceUrl: string | null | undefined,
  locationInfo?: string | null,
  sourceDocName?: string | null
): string | null {
  if (!sourceUrl && !sourceDocName) return null;

  const apiBaseUrl = getBaseUrl();
  const page = extractPageNumber(locationInfo);
  const pageFragment = page ? `#page=${page}` : "";

  if (sourceUrl?.startsWith("/api/")) {
    return `${apiBaseUrl}${sourceUrl}${pageFragment}`;
  }

  // Azure Blob Storage (prod) or Azurite (local dev)
  const azureMatch = sourceUrl?.match(/blob\.core\.windows\.net\/[^/]+\/(.+)$/);
  // Ingestion running inside Docker writes blob URLs with host "azurite"
  // (the compose service name), not "localhost" — match both.
  const azuriteMatch = sourceUrl?.match(/(?:localhost|azurite):\d+\/devstoreaccount1\/[^/]+\/(.+)$/);

  const blobPathMatch = azureMatch?.[1] || azuriteMatch?.[1];

  if (blobPathMatch) {
    const blobPath = safeDecode(blobPathMatch);
    const encodedPath = blobPath
      .split("/")
      .map((segment) => encodeURIComponent(segment))
      .join("/");
    return `${apiBaseUrl}/api/documents/download/${encodedPath}${pageFragment}`;
  }

  if (sourceUrl?.startsWith("http://") || sourceUrl?.startsWith("https://")) {
    return `${sourceUrl}${pageFragment}`;
  }

  const rawFilename = sourceUrl ? sourceUrl.split(/[/\\]/).pop() : sourceDocName;

  if (!rawFilename) return null;

  const filename = encodeURIComponent(safeDecode(rawFilename));

  return `${apiBaseUrl}/api/documents/download/${filename}${pageFragment}`;
}

/**
 * Extracts the blob path from an `/api/documents/download/{path}` URL.
 */
export function extractBlobPath(url: string): string | null {
  const match = url.match(/\/api\/documents\/download\/(.+?)(?:#|$)/);

  return match ? safeDecode(match[1]) : null;
}

/**
 * Extracts the `#fragment` (e.g. `#page=3`) from a URL, including the leading `#`.
 */
export function extractFragment(url: string): string {
  const match = url.match(/#(.+)$/);

  return match ? `#${match[1]}` : "";
}

/**
 * Opens a document URL, delegating the platform-specific open action to `openFn`.
 *
 * For API URLs: calls `openFn` with `/view-document/{path}{#fragment}`.
 * For external URLs: calls `openFn` with the URL directly.
 *
 * On web, pass `(url) => window.open(url, "_blank", "noopener,noreferrer")`.
 * On React Native, pass `(url) => Linking.openURL(url)`.
 */
export function openDocument(url: string, openFn: (url: string) => void): void {
  if (!isApiUrl(url)) {
    openFn(url);
    return;
  }

  const blobPath = extractBlobPath(url);

  if (blobPath) {
    const fragment = extractFragment(url);
    openFn(`/view-document/${blobPath}${fragment}`);
  } else {
    console.error("Failed to extract blob path from URL:", url);
  }
}
